import React from "react";
import { motion } from "framer-motion";
import { useIsFetching } from "@tanstack/react-query";
import { useDelayedFlag } from "../hooks/useDelayedFlag";

// Build info injected by Vite at build time (declared in vite-env.d.ts)
const APP_VERSION = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev";
const BUILD_HASH = typeof __BUILD_HASH__ !== "undefined" ? __BUILD_HASH__ : "";

// Footer Component
export const Footer: React.FC = () => {
  return (
    <footer className="bg-bg-2/95 backdrop-blur-md border-t border-border px-4 py-2">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-fg-muted">Boston, MA</span>
          <span className="text-[11px] text-fg-muted">
            v{APP_VERSION}
            {BUILD_HASH ? `.${BUILD_HASH}` : ""}
          </span>
        </div>

        <div className="flex items-center">
          <nav aria-label="Legal" className="flex items-center gap-3">
            <a
              href="https://a2zradiology.ai/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-fg-muted hover:text-fg transition-colors duration-200"
            >
              Privacy Policy
            </a>
            <a
              href="https://a2zradiology.ai/cookie-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-fg-muted hover:text-fg transition-colors duration-200"
            >
              Cookie Policy
            </a>
            <button
              data-cc="show-preferencesModal"
              className="text-[11px] text-fg-muted hover:text-fg transition-colors duration-200 cursor-pointer"
            >
              Cookie Preferences
            </button>
          </nav>
        </div>

        <p className="text-[11px] text-fg-muted">
          &copy; {new Date().getFullYear()} a2z Radiology AI
        </p>
      </div>
    </footer>
  );
};

// The single global "something upstream is working" signal. A 2px top-edge
// indeterminate indigo sweep that appears only after 300ms of sustained
// fetching — so cached/fast responses never paint it. (loading-no-phase-changes)
export const FetchProgressBar: React.FC = () => {
  const isFetching = useIsFetching();
  const visible = useDelayedFlag(isFetching > 0, 300);
  if (!visible) return null;
  return (
    <div
      data-testid="fetch-progress"
      aria-hidden
      className="fixed top-0 left-0 right-0 h-0.5 z-9999 pointer-events-none overflow-hidden"
    >
      <div className="fetch-sweep absolute inset-0" />
    </div>
  );
};

// ShinyButton Component
interface ShinyButtonProps {
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}

export const ShinyButton: React.FC<ShinyButtonProps> = ({ onClick, children, className = "" }) => (
  <motion.button
    className={`relative overflow-hidden bg-primary text-white font-bold py-3 px-6 text-lg transition-all flex items-center shine-effect ${className}`}
    onClick={onClick}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
  >
    <div className="flex items-center relative z-10">{children}</div>
  </motion.button>
);
